import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { MidtransUtils } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import QrCodeImage from '@renderer/components/QrCodeImage'
import * as stateUploader from '@renderer/utils/pipeline/stateUploader'
import BkScreen from './BkScreen'
import { timeoutSeconds, useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import BkPixel from './BkPixel'
import BkPrintTags from './BkPrintTags'
import { printOptionsOf } from './printOptions'
import { photoColumns } from './cutCategories'
import { nextBkStep } from './flow'
import { canStitch, priceDigits, stitchText } from './pixelType'
import BkVoucherPad from './BkVoucherPad'
import { BK_ASSET, BK_HIT, bkHit } from './layout'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

/** How often to ask the gateway whether the guest has paid. */
const POLL_MS = 5000

/**
 * How long the print count has to sit still before a new QR is minted. Every
 * mint is a real gateway order; a guest tapping 1-2-3 should cost one, not three.
 */
const REMINT_DEBOUNCE_MS = 600

/** One QR minted during this visit, and what paying it buys. */
interface MintedOrder {
  quantity: number
  amount: number
}

/**
 * Screen 3 — "how to pay": a QR plaque and a cash plaque. It follows the cut,
 * whose price it charges (see `pricedByCut`).
 *
 * The QR is a real QRIS charge. The project is stamped MYR, which
 * `resolvePaymentSetupTierFromCurrency` maps to `cross_border_qris`, so the
 * price shows in ringgit while the gateway settles in rupiah — the plate's "RM"
 * is correct and the rupiah figure never reaches the guest.
 */
const Payment: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { config, frames } = useConfigStore()
  const frameId = useSessionStateStore((s) => s.userPreferences?.frameId)

  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)
  const [expired, setExpired] = useState(false)
  const [failure, setFailure] = useState('')
  const [paid, setPaid] = useState(false)
  const [voucherOpen, setVoucherOpen] = useState(false)
  const [voucherBusy, setVoucherBusy] = useState(false)
  const [voucherError, setVoucherError] = useState('')
  const [retry, setRetry] = useState(0)
  const mountedRef = useRef(true)
  /**
   * Every order minted while the guest stood here, not just the one on screen.
   * A guest can scan the QR and then change the print count before paying —
   * the old code is off the screen but still live at the gateway, and a
   * payment against it has to be recognised rather than lost.
   */
  const ordersRef = useRef(new Map<string, MintedOrder>())
  /** Bumped per mint; a response for anything but the latest is not shown. */
  const mintSeqRef = useRef(0)
  /** Two polls can be in flight on a slow network; only one may settle. */
  const settledRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return (): void => {
      mountedRef.current = false
    }
  }, [])

  const paymentSettings = config?.pages?.payment?.settings
  /**
   * The one page where "no timeout" cannot mean no timeout: a gateway charge
   * has to expire. So null falls back to stock's 120s — which also replaces a
   * 300s default here that never matched stock.
   */
  const chargeSeconds = timeoutSeconds(paymentSettings?.paymentTimeout) ?? 120
  const currency = config?.resolvedCurrency || 'MYR'

  const options = useMemo(
    () => printOptionsOf(config?.pages?.checkout?.settings?.packages || []),
    [config]
  )

  /**
   * Priced by the cut. The cut chosen before this screen is a 6x8 sheet whose
   * frame has one photo column per paid strip, and the package whose quantity
   * is that strip count is its price: "2 strip RM25" is the quantity-2
   * package. The guest then gets ONE sheet, whatever the quantity says.
   *
   * A project whose packages do not name the cut's strip count keeps the old
   * behaviour: a note per print count, and the count is the number of prints.
   */
  const strips = useMemo(
    () => photoColumns(frames.find((f) => f._id === frameId)).length,
    [frames, frameId]
  )
  const cutOption = options.find((o) => o.quantity === strips)
  const pricedByCut = Boolean(cutOption)
  const copiesFor = useCallback((q: number): number => (pricedByCut ? 1 : q), [pricedByCut])

  const [quantity, setQuantity] = useState(() => cutOption?.quantity ?? options[0]?.quantity ?? 1)
  // The catalogue can arrive after mount; never sit on a count it does not offer.
  useEffect(() => {
    if (cutOption) {
      if (quantity !== cutOption.quantity) setQuantity(cutOption.quantity)
      return
    }
    if (options.length && !options.some((o) => o.quantity === quantity)) {
      setQuantity(options[0].quantity)
    }
  }, [cutOption, options, quantity])

  const priceOf = useCallback(
    (q: number): number => options.find((o) => o.quantity === q)?.price ?? 0,
    [options]
  )

  const goOn = useCallback((): void => {
    uploadSessionState()
    navigate(`/${projectId}/${nextBkStep(config?.pages, 'payment')}`)
  }, [config, navigate, projectId, uploadSessionState])

  /** Mint a QR for `q` prints. Called on arrival and whenever the count settles. */
  const mint = useCallback(
    async (q: number): Promise<void> => {
      const seq = ++mintSeqRef.current
      const live = useSessionStateStore.getState()
      const discount = live.transaction?.discount || 0
      const amount = Math.max(0, priceOf(q) - discount)

      // A booth with no price configured must not silently hand out free prints:
      // say so, and let the operator fix the package rather than the guest walk.
      if (amount <= 0) {
        logActivity('BK payment skipped: no price configured on the project', { level: 'error' })
        setFailure('No price configured')
        return
      }

      try {
        const data = await MidtransUtils.initiatePayment(
          amount,
          live.id || '',
          chargeSeconds,
          projectId || '',
          live.userPreferences?.filterId || '',
          live.userPreferences?.frameId || '',
          discount
        )
        // The guest changed the count while this was in flight. Its QR was never
        // shown, so nobody can pay it; the newer mint owns the screen.
        if (seq !== mintSeqRef.current || !mountedRef.current) return

        if (!data.order_id || !data.qr_url) {
          logActivity('BK payment: gateway returned no QR', { level: 'error' })
          setFailure('Could not open QR — tap to retry')
          return
        }

        // Double slashes survive some gateway responses and break the proxy.
        const cleanQr = data.qr_url.replace(/([^:]\/)\/+/g, '$1')

        setSessionState({
          transaction: {
            orderId: data.order_id,
            qrUrl: cleanQr,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending',
            amount,
            discount
          },
          userPreferences: { copies: copiesFor(q) }
        })
        ordersRef.current.set(data.order_id, { quantity: q, amount })
        logActivity('BK payment started', {
          orderId: data.order_id,
          amount,
          copies: copiesFor(q),
          strips: pricedByCut ? q : undefined,
          currency
        })
        setQrUrl(cleanQr)
        setExpired(false)
        setPolling(true)
      } catch (error) {
        if (seq !== mintSeqRef.current) return
        const message = error instanceof Error ? error.message : String(error)
        logActivity(`BK payment failed to start: ${message}`, { level: 'error' })
        setFailure(message)
      }
    },
    [chargeSeconds, copiesFor, currency, logActivity, priceOf, pricedByCut, projectId, setSessionState]
  )

  // The QR is the screen's purpose: mint on arrival, and again whenever the
  // print count settles. Reached through a ref so a new `mint` identity (config
  // arriving) does not mint a second order for the same count.
  const mintRef = useRef(mint)
  mintRef.current = mint
  const firstMintRef = useRef(true)
  useEffect(() => {
    if (paid || !options.length) return
    const wait = firstMintRef.current ? 0 : REMINT_DEBOUNCE_MS
    firstMintRef.current = false
    const timer = setTimeout(() => void mintRef.current(quantity), wait)
    return (): void => clearTimeout(timer)
    // `paid` is deliberately absent: paying ends minting, it never starts it.
  }, [quantity, retry, options.length])

  const chooseQuantity = (q: number): void => {
    if (paid || q === quantity) return
    logActivity(`BK print count chosen: ${q}`, { price: priceOf(q) })
    // Take the old code off the screen at once: a guest must never scan a QR
    // whose price no longer matches the note they just picked.
    mintSeqRef.current++
    setQrUrl(null)
    setExpired(false)
    setFailure('')
    setQuantity(q)
    setSessionState({ userPreferences: { copies: copiesFor(q) } })
  }

  const checkStatus = useCallback(async (): Promise<void> => {
    for (const [orderId, order] of [...ordersRef.current]) {
      try {
        const status = await MidtransUtils.checkPaymentStatus(orderId)
        if (!mountedRef.current) return

        if (status.paid) {
          if (settledRef.current) return
          settledRef.current = true
          // Whichever QR was paid decides the prints — it may be an older one
          // than the code on screen.
          ordersRef.current.clear()
          setPolling(false)
          setPaid(true)
          setSessionState({
            transaction: { orderId, status: 'settlement', amount: order.amount },
            userPreferences: { copies: copiesFor(order.quantity) }
          })
          logActivity('BK payment settled', { orderId, copies: copiesFor(order.quantity) })
          uploadSessionState()
          setTimeout(() => mountedRef.current && goOn(), 1500)
          return
        }
        if (status.isExpired) {
          ordersRef.current.delete(orderId)
          if (orderId === useSessionStateStore.getState().transaction?.orderId) {
            setExpired(true)
            setSessionState({ transaction: { status: 'expire' } })
            logActivity('BK payment expired', { orderId })
            uploadSessionState()
          }
        }
      } catch (error) {
        // A dropped poll is normal on a booth's network; the next tick retries.
        console.warn('[BK] payment status check failed:', error)
      }
    }
    if (!ordersRef.current.size) setPolling(false)
  }, [copiesFor, goOn, logActivity, setSessionState, uploadSessionState])

  useInterval(() => void checkStatus(), polling ? POLL_MS : null)

  // Counts the charge down. At zero, ask the gateway once more straight away —
  // as stock does — rather than wait for the next poll, so a guest who paid in
  // the last seconds is not shown "expired".
  const remaining = useBkPageTimeout(
    chargeSeconds,
    () => void checkStatus(),
    Boolean(qrUrl) && !expired && !paid
  )

  /**
   * The second plaque takes a CASH VOUCHER, not cash.
   *
   * The operator takes the money and hands over a code; redeeming it here is
   * what settles the session. That is the same contract as the stock
   * `view/Payment.tsx` voucher path — `/api/promos/apply` decides whether a
   * code is real and whether it is a cash voucher — so a guest cannot simply
   * tap "cash" and walk to the camera without paying.
   */
  const redeemVoucher = async (raw: string): Promise<void> => {
    const code = raw.trim().toUpperCase()
    if (!code || voucherBusy || paid) return
    setVoucherBusy(true)
    setVoucherError('')

    try {
      // The voucher API wants the subtotal in the project's NATIVE currency:
      // the package price for the count on screen, never `transaction.amount`,
      // which can hold the gateway settlement figure once a QR is minted.
      const subtotal = Math.max(0, priceOf(quantity))

      const res = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, projectId: projectId || '', amount: Math.round(subtotal) })
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok || !data.isValid) {
        const message = data.message || data.error || 'Invalid voucher code'
        setVoucherError(message)
        logActivity(`BK voucher rejected: ${code}`, { level: 'warning' })
        return
      }

      if (!data.isCash) {
        // A discount voucher lowers the bill but does not pay it. Send the
        // guest to the QR for the remainder rather than letting them through.
        setVoucherError('That is a discount code — please pay the balance by QR')
        logActivity(`BK non-cash voucher offered at payment: ${code}`)
        return
      }

      if (settledRef.current) return
      settledRef.current = true
      logActivity('BK cash voucher redeemed', { code, amount: subtotal, copies: copiesFor(quantity) })
      ordersRef.current.clear()
      setSessionState({
        userPreferences: { copies: copiesFor(quantity) },
        transaction: {
          status: 'settlement',
          currency,
          amount: subtotal,
          voucherCode: code,
          voucherId: data.promoId,
          voucherType: 'cash',
          voucherValue: data.value
        }
      })
      stateUploader.uploadSessionState(useSessionStateStore.getState().id || '')
      setPaid(true)
      setVoucherOpen(false)
      setTimeout(() => mountedRef.current && goOn(), 1200)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setVoucherError(message)
      logActivity(`BK voucher check failed: ${message}`, { level: 'error' })
    } finally {
      setVoucherBusy(false)
    }
  }

  const price = priceOf(quantity)
  const amountLabel = `${currency === 'MYR' ? 'RM' : currency}${priceDigits(price)}`

  return (
    <BkScreen plate="payment.webp">
      {/* QR and price, inside the plaque's cream panel — not over its frame. */}
      <div
        style={bkHit(BK_HIT.payment.qrContent)}
        className="flex flex-col items-center justify-center gap-4"
      >
        {qrUrl && !expired ? (
          // QrCodeImage wraps its <img> in a `relative h-full w-full` div, so it
          // fills whatever box it is given. Sizing only the inner image left the
          // wrapper stretched over the whole panel and pinned the code to the
          // panel's top-left corner — give the wrapper the size instead.
          <div className="h-[250px] w-[250px] shrink-0">
            <QrCodeImage
              qrUrl={qrUrl}
              className="h-full w-full object-contain"
              onEvent={qrActivityLogger(logActivity, 'bingkai-kenangan-payment')}
            />
          </div>
        ) : (
          <button
            aria-label="Show QR"
            onClick={() => {
              setFailure('')
              setRetry((n) => n + 1)
            }}
            className="flex h-[270px] w-[270px] items-center justify-center bg-transparent px-2 text-center text-[22px] leading-snug text-[#6C1824]"
          >
            {expired ? 'Expired — tap to retry' : failure || 'Loading QR…'}
          </button>
        )}
        {/* The price in the plates' block lettering; a currency without
            glyphs falls back to type rather than render blank. */}
        {canStitch(amountLabel) ? (
          <BkPixel rows={stitchText(amountLabel)} block={5} />
        ) : (
          <span className="text-[30px] font-semibold text-[#6C1824]">{amountLabel}</span>
        )}
      </div>

      {/* How many prints. Only when the project sells more than one count and
          the price is not the cut's — a cut is one sheet. */}
      {!pricedByCut && options.length > 1 && (
        <BkPrintTags
          options={options}
          selected={quantity}
          onSelect={chooseQuantity}
          disabled={paid}
        />
      )}

      {/* Voucher — the operator takes the cash and hands over a code. A swan,
          like every other button on these plates, lettered "redeem" in their
          own hand, across the foot of the cash plaque. */}
      <button
        aria-label="Redeem voucher"
        onClick={() => setVoucherOpen(true)}
        disabled={paid}
        style={bkHit(BK_HIT.payment.redeem)}
        className="cursor-pointer bg-transparent p-0 disabled:cursor-default disabled:opacity-50"
      >
        <img src={BK_ASSET('redeem-swan.webp')} alt="" aria-hidden className="h-full w-full" />
      </button>

      {voucherOpen && !paid && (
        <BkVoucherPad
          isLoading={voucherBusy}
          error={voucherError}
          onInputChange={() => setVoucherError('')}
          onEnter={redeemVoucher}
          onClose={() => {
            setVoucherOpen(false)
            setVoucherError('')
          }}
        />
      )}

      {paid && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45">
          <span className="text-[64px] font-semibold text-white">Paid</span>
        </div>
      )}
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Payment
